import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { Loader2, Eye, EyeOff, Check, X, Copy, Download, Shield, AlertTriangle } from "lucide-react";

/**
 * Pricing-First Phase B — public setup page reached via the magic link the
 * server emailed after Stripe payment: /setup-password/:token
 *
 * Flow:
 *   1. On mount: GET /api/pro/setup-token/:token
 *      - 200 → render credentials form, show the prefilled email (read-only)
 *      - 410 → "expired" / "already used" UI
 *      - 404 → "not found" UI
 *   2. User submits username + password → POST /api/pro/setup-password
 *      - 200 → reveal recovery codes, then redirect to /pro/inbox
 *      - 409 → username taken (token still valid, retry)
 *      - 410 → token raced & finalized; redirect to /pro/login
 */
export default function SetupPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  // ── token validation ──
  const [tokenState, setTokenState] = useState("checking"); // checking | valid | invalid
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");

  // ── credentials form ──
  const [step, setStep] = useState("credentials"); // credentials | recovery
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState(null); // null | checking | available | unavailable | invalid
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── recovery codes ──
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesConfirmed, setCodesConfirmed] = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setTokenState("invalid");
        setTokenError("Setup link is missing.");
        return;
      }
      try {
        const { data } = await api.get(`/pro/setup-token/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (data?.valid) {
          setEmail(data.email || "");
          setTokenState("valid");
        } else {
          setTokenState("invalid");
          setTokenError("Setup link is no longer valid.");
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        setTokenState("invalid");
        if (status === 410) setTokenError(detail || "This setup link has expired or already been used.");
        else if (status === 404) setTokenError(detail || "Setup link not found.");
        else setTokenError(detail || "Could not validate the setup link. Please try again.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Username availability check (debounced)
  const checkUsername = useCallback(async (val) => {
    if (!val || val.length < 3) {
      setUsernameStatus(val.length > 0 ? "invalid" : null);
      return;
    }
    setUsernameStatus("checking");
    try {
      const { data } = await api.post("/pro/check-username", { username: val });
      setUsernameStatus(data?.available ? "available" : "unavailable");
    } catch {
      setUsernameStatus("available"); // fail open — backend will revalidate
    }
  }, []);

  const handleUsernameChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9._-]/g, "");
    setUsername(clean);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!clean) { setUsernameStatus(null); return; }
    debounceRef.current = setTimeout(() => checkUsername(clean), 400);
  };

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async () => {
    if (!passwordValid || !passwordsMatch || usernameStatus !== "available") return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/pro/setup-password", {
        token,
        username: username.trim(),
        password,
        password_confirm: confirmPassword,
      });
      setRecoveryCodes(data.recovery_codes || []);
      setStep("recovery");
      toast.success("Account activated");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setUsernameStatus("unavailable");
        toast.error("This username is not available");
      } else if (status === 410) {
        // token raced — surface and bounce to login
        toast.error(err?.response?.data?.detail || "Setup link no longer valid.");
        setTokenState("invalid");
        setTokenError(err?.response?.data?.detail || "Setup link no longer valid.");
      } else {
        toast.error(getErrorMessage(err, "Setup failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied");
  };

  const handleDownloadCodes = () => {
    const text = [
      "QuickInbox PRO — Recovery Codes",
      `Username: ${username}`,
      `Generated: ${new Date().toISOString()}`,
      "",
      "Each code can only be used ONCE.",
      "",
      ...recoveryCodes.map((c, i) => `${i + 1}. ${c}`),
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quickinbox-recovery-codes-${username}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recovery codes downloaded");
  };

  const handleFinish = () => {
    if (!codesConfirmed) {
      toast.error("Please confirm you've saved your recovery codes");
      return;
    }
    navigate("/pro/inbox", { replace: true });
  };

  // ── render guards ──
  if (tokenState === "checking") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="setup-password-checking">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-zinc-500">Verifying your setup link…</p>
        </div>
      </div>
    );
  }

  if (tokenState === "invalid") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="setup-password-invalid">
        <div className="max-w-sm text-center space-y-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 flex items-center justify-center">
            <AlertTriangle size={28} className="text-red-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold tracking-tight text-[#111111]">
              Setup link no longer valid
            </h1>
            <p className="text-sm text-zinc-500">{tokenError}</p>
          </div>
          <div className="space-y-2">
            <Button
              onClick={() => navigate("/pro/login")}
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="setup-password-go-login"
            >
              Go to login
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/pricing")}
              className="w-full h-10"
              data-testid="setup-password-back-pricing"
            >
              Back to pricing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── credentials step ──
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md" data-testid="setup-password-page">
        {step === "credentials" ? (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 mx-auto rounded-xl bg-zinc-900 flex items-center justify-center">
                <Shield size={22} className="text-white" />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#111111]">
                Set up your PRO account
              </h1>
              <p className="text-sm text-zinc-500">
                Choose your username and password to unlock your inbox.
              </p>
            </div>

            {email && (
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2.5 flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-zinc-500">Email</span>
                <span className="font-mono text-sm text-zinc-700 truncate" data-testid="setup-password-email">{email}</span>
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Username</Label>
                <Input
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="choose a username"
                  className="font-mono"
                  autoFocus
                  data-testid="setup-password-username"
                />
                <div className="h-4 flex items-center gap-1.5">
                  {usernameStatus === "checking" && <Loader2 size={12} className="text-zinc-500 animate-spin" />}
                  {usernameStatus === "available" && <><Check size={12} className="text-green-500" /><span className="text-[11px] text-green-600">Available</span></>}
                  {usernameStatus === "unavailable" && <><X size={12} className="text-red-500" /><span className="text-[11px] text-red-500">Not available</span></>}
                  {usernameStatus === "invalid" && <><X size={12} className="text-zinc-500" /><span className="text-[11px] text-zinc-500">At least 3 characters</span></>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="pr-10"
                    data-testid="setup-password-pwd"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password.length > 0 && !passwordValid && (
                  <p className="text-[11px] text-red-500">Password must be at least 8 characters</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Confirm password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  data-testid="setup-password-confirm"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-[11px] text-red-500">Passwords do not match</p>
                )}
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || usernameStatus !== "available" || !passwordValid || !passwordsMatch}
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="setup-password-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : "Activate account"}
            </Button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="setup-password-recovery-step">
            <div className="space-y-2">
              <h1 className="text-xl font-semibold tracking-tight text-[#111111]">
                Save your recovery codes
              </h1>
              <p className="text-sm text-zinc-500">
                These codes are the <strong className="text-[#555555]">only way</strong> to recover your account if you forget your password. Each code can only be used once.
              </p>
            </div>

            <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-4" data-testid="setup-password-recovery-codes">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, i) => (
                  <div key={i} className="font-mono text-sm text-[#555555] bg-white rounded px-2.5 py-1.5 border border-[#E5E5E5] text-center tracking-wider">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={handleCopyCodes} data-testid="setup-password-copy">
                <Copy size={13} className="mr-1.5" /> Copy
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={handleDownloadCodes} data-testid="setup-password-download">
                <Download size={13} className="mr-1.5" /> Download
              </Button>
            </div>

            <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Checkbox
                id="codes-confirmed"
                checked={codesConfirmed}
                onCheckedChange={setCodesConfirmed}
                data-testid="setup-password-confirm-checkbox"
              />
              <label htmlFor="codes-confirmed" className="text-xs text-amber-800 leading-relaxed cursor-pointer">
                I have saved my recovery codes in a secure location.
              </label>
            </div>

            <Button
              onClick={handleFinish}
              disabled={!codesConfirmed}
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="setup-password-finish"
            >
              Continue to inbox
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
